import { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Float, Text, Sphere, Box, Cylinder } from '@react-three/drei';
import * as THREE from 'three';

// Robot head
function RobotHead() {
  const headRef = useRef<THREE.Group>(null);
  
  useFrame((state) => {
    if (headRef.current) {
      headRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.2;
      headRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.3) * 0.05;
    }
  });
  
  return (
    <group ref={headRef}>
      {/* Head base */}
      <Box args={[0.8, 0.7, 0.6]}>
        <meshStandardMaterial color="#e2e8f0" />
      </Box>
      {/* Face screen */}
      <Box args={[0.6, 0.4, 0.05]} position={[0, 0.05, 0.3]}>
        <meshStandardMaterial color="#1e293b" emissive="#0f172a" emissiveIntensity={0.3} />
      </Box>
      {/* Eyes */}
      <Sphere args={[0.08]} position={[-0.15, 0.1, 0.33]}>
        <meshStandardMaterial color="#3b82f6" emissive="#2563eb" emissiveIntensity={0.8} />
      </Sphere>
      <Sphere args={[0.08]} position={[0.15, 0.1, 0.33]}>
        <meshStandardMaterial color="#3b82f6" emissive="#2563eb" emissiveIntensity={0.8} />
      </Sphere>
      {/* Antenna */}
      <Cylinder args={[0.02, 0.02, 0.3]} position={[0, 0.5, 0]}>
        <meshStandardMaterial color="#64748b" />
      </Cylinder>
      <Sphere args={[0.06]} position={[0, 0.65, 0]}>
        <meshStandardMaterial color="#F97316" emissive="#ea580c" emissiveIntensity={0.5} />
      </Sphere>
    </group>
  );
}

// Robot body
function RobotBody() {
  return (
    <group position={[0, -0.8, 0]}>
      {/* Main body */}
      <Box args={[0.6, 0.8, 0.4]}>
        <meshStandardMaterial color="#cbd5e1" />
      </Box>
      {/* Chest panel */}
      <Box args={[0.4, 0.3, 0.05]} position={[0, 0.1, 0.2]}>
        <meshStandardMaterial color="#1e293b" />
      </Box>
      {/* Status light */}
      <Sphere args={[0.04]} position={[0, 0.1, 0.23]}>
        <meshStandardMaterial color="#22c55e" emissive="#16a34a" emissiveIntensity={0.8} />
      </Sphere>
    </group>
  );
}

// Robot arms
function RobotArms() {
  const leftArmRef = useRef<THREE.Group>(null);
  const rightArmRef = useRef<THREE.Group>(null);
  
  useFrame((state) => {
    if (leftArmRef.current && rightArmRef.current) {
      leftArmRef.current.rotation.z = Math.sin(state.clock.elapsedTime) * 0.1 + 0.2;
      rightArmRef.current.rotation.z = -Math.sin(state.clock.elapsedTime) * 0.1 - 0.2;
    }
  });
  
  return (
    <>
      <group ref={leftArmRef} position={[-0.5, -0.5, 0]}>
        <Cylinder args={[0.06, 0.06, 0.6]} rotation={[0, 0, Math.PI / 6]}>
          <meshStandardMaterial color="#94a3b8" />
        </Cylinder>
        <Sphere args={[0.08]} position={[-0.15, -0.3, 0]}>
          <meshStandardMaterial color="#64748b" />
        </Sphere>
      </group>
      <group ref={rightArmRef} position={[0.5, -0.5, 0]}>
        <Cylinder args={[0.06, 0.06, 0.6]} rotation={[0, 0, -Math.PI / 6]}>
          <meshStandardMaterial color="#94a3b8" />
        </Cylinder>
        <Sphere args={[0.08]} position={[0.15, -0.3, 0]}>
          <meshStandardMaterial color="#64748b" />
        </Sphere>
      </group>
    </>
  );
}

// Floating AI text
function FloatingText() {
  const texts = ['AI', 'ML', 'Learn', 'Grow'];
  
  return (
    <>
      {texts.map((text, i) => (
        <Float
          key={i}
          speed={1.5}
          rotationIntensity={0.3}
          floatIntensity={0.3}
          position={[
            Math.cos((i / texts.length) * Math.PI * 2) * 1.5,
            Math.sin((i / texts.length) * Math.PI * 2) * 0.5 + 0.5,
            Math.sin((i / texts.length) * Math.PI * 2) * 0.5
          ]}
        >
          <Text
            fontSize={0.2}
            color="#F97316"
            anchorX="center"
            anchorY="middle"
            font="https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuGKYAZ9hjp-Ek-_EeA.woff2"
          >
            {text}
          </Text>
        </Float>
      ))}
    </>
  );
}

// Particles
function Particles() {
  const count = 20;
  const positions = useMemo(() => {
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 4;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 3;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 2;
    }
    return pos;
  }, []);

  const pointsRef = useRef<THREE.Points>(null);

  useFrame((state) => {
    if (pointsRef.current) {
      pointsRef.current.rotation.y = state.clock.elapsedTime * 0.03;
    }
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={count}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.04}
        color="#3b82f6"
        transparent
        opacity={0.5}
        sizeAttenuation
      />
    </points>
  );
}

// Main scene
function Scene() {
  return (
    <>
      <ambientLight intensity={0.5} />
      <directionalLight position={[5, 5, 5]} intensity={0.8} />
      <pointLight position={[-5, 3, -5]} intensity={0.5} color="#F97316" />
      <pointLight position={[5, 3, 5]} intensity={0.4} color="#3b82f6" />
      
      <Float speed={2} rotationIntensity={0.1} floatIntensity={0.2}>
        <group position={[0, 0.5, 0]}>
          <RobotHead />
          <RobotBody />
          <RobotArms />
        </group>
      </Float>
      
      <FloatingText />
      <Particles />
      
      <OrbitControls 
        enableZoom={false} 
        enablePan={false}
        maxPolarAngle={Math.PI / 2}
        minPolarAngle={Math.PI / 3}
        autoRotate
        autoRotateSpeed={0.3}
      />
    </>
  );
}

export default function AITutor() {
  return (
    <div className="w-full h-[400px] md:h-[500px]">
      <Canvas camera={{ position: [0, 1, 4], fov: 50 }} gl={{ antialias: true }}>
        <Scene />
      </Canvas>
    </div>
  );
}